import {
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  LeftOutlined,
  MessageOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearCache } from 'ahooks';
import { AxiosError } from 'axios';
import PageTopBar from 'components/navigation/PageTopBar';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { MultiSelect } from 'components/ui/multi-select';
import { Progress } from 'components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Spinner } from 'components/ui/spinner';
import { Textarea } from 'components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import useIsKeyboardVisible from 'hooks/useIsKeyboardVisible';
import { cn } from 'lib/utils';
import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import posthog from 'posthog-js';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import {
  QuestionnaireAnswers,
  QuestionnaireQuestion,
  QuestionnaireResponseDTO,
  QuestionnaireSection,
} from 'services/api/models/QuestionnaireDTO';
import { routes } from 'services/RouteService';
import { contentActions } from 'store/content';
import { selectCurrentUser } from 'store/users/selectors';

/** Safely renders a string that may contain only <b> and <br> tags. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(<b>.*?<\/b>|<br\s*\/?>)/s);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('<b>')) {
          return <b key={i}>{part.slice(3, -4)}</b>;
        }
        if (part.startsWith('<br')) {
          return <br key={i} />;
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

const BIRTH_YEARS = Array.from({ length: new Date().getFullYear() - 1900 }, (_, index) => `${1900 + index}`).reverse();

const pageContainerClassName = 'flex h-full flex-col items-center overflow-hidden bg-background';
const questionLabelClassName = 'm-0 text-base font-medium text-foreground';
const greetingContainerClassName = 'flex flex-col gap-4 px-6 py-10';
const greetingDescriptionListClassName = 'm-0 flex list-none flex-col gap-6 p-0';
const greetingDescriptionItemClassName = 'flex items-start gap-3';
const greetingDescriptionIconClassName = 'mt-1 shrink-0 text-lg text-foreground';
const greetingDescriptionTextClassName = 'm-0 text-sm leading-normal text-foreground opacity-90';
const navigationBarClassName =
  'sticky bottom-0 flex w-full max-w-content flex-row-reverse items-center justify-between gap-3 bg-background px-5 py-3';
const navigationBarInlineClassName =
  'flex w-full max-w-content flex-row-reverse items-center justify-between gap-3 pt-3';
const savingIndicatorClassName = (visible: boolean) =>
  cn('text-base text-subtle-foreground transition-opacity duration-[400ms]', visible ? 'opacity-100' : 'opacity-0');
const choiceButtonClassName = (selected: boolean) =>
  cn(
    'mb-2 h-auto min-h-11 w-full px-3.5 py-2 text-center leading-normal whitespace-normal',
    selected ? 'border-primary bg-success-muted font-semibold text-foreground' : 'bg-card text-subtle-foreground',
  );
// Bordered horizontal choice group ("Oui" / "Non", menopause answers…): every
// option shares the row, the chosen one is outlined in the brand colour.
// The questionnaire's segmented row is its own design, not the stock toggle
// group; a candidate for a variant.
const horizontalGroupClassName = 'w-full items-stretch overflow-hidden rounded-md shadow-none';
const horizontalItemClassName =
  'h-auto flex-1 bg-card px-1.5 py-2.5 text-center text-base leading-tight whitespace-normal text-subtle-foreground data-[state=on]:bg-card data-[state=on]:font-semibold data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary data-[state=on]:ring-inset';
const alertClassName = 'mt-8 mb-4 rounded-md';

const toToggleValue = (value: unknown) => (value === true ? 'true' : value === false ? 'false' : '');

function NoteAlert({
  type,
  text,
  className,
}: {
  type: 'warning' | 'info' | 'error';
  text: string;
  className?: string;
}) {
  return (
    <Alert variant={type === 'error' ? 'destructive' : type} className={cn(alertClassName, className)}>
      {type === 'info' ? (
        <Info aria-hidden />
      ) : type === 'error' ? (
        <CircleAlert aria-hidden />
      ) : (
        <TriangleAlert aria-hidden />
      )}
      <AlertDescription>
        {/* One grid item: the description lays its children out as rows, so bare inline parts would each get a line */}
        <p>
          <RichText text={text} />
        </p>
      </AlertDescription>
    </Alert>
  );
}

function isQuestionVisible(question: QuestionnaireQuestion, answers: QuestionnaireAnswers): boolean {
  if (!question.condition) return true;
  const { question_id, value, contains, not_equals } = question.condition as any;
  const answer = answers[question_id];
  if (value !== undefined) return answer === value;
  if (contains !== undefined) return Array.isArray(answer) && answer.includes(contains);
  if (not_equals !== undefined) return answer !== not_equals && answer !== undefined && answer !== null;
  return true;
}

interface SectionFormProps {
  section: QuestionnaireSection;
  answers: QuestionnaireAnswers;
  onChange: (questionId: string, value: QuestionnaireAnswers[string]) => void;
}

/** The year picker; opening it scrolls its question block to the top so the list has room. */
function YearSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={wrapRef} className="w-full">
      <Select
        value={value ?? ''}
        onValueChange={onChange}
        onOpenChange={open => {
          if (open)
            setTimeout(
              () => wrapRef.current?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
              50,
            );
        }}
      >
        <SelectTrigger className="w-full data-[size=default]:h-11">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {BIRTH_YEARS.map(year => (
            <SelectItem key={year} value={year} className="whitespace-normal">
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SectionForm({ section, answers, onChange }: SectionFormProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5">
      {section.note && <NoteAlert type="warning" text={section.note} />}
      {section.questions
        .filter(q => isQuestionVisible(q, answers))
        .map(question => {
          if (question.type === 'separator') {
            return <hr key={question.id} className="my-6 border-0 border-t border-border" />;
          }
          if (question.type === 'info') {
            return (
              <div key={question.id} data-info-block>
                <NoteAlert type="info" text={question.label} />
              </div>
            );
          }
          if (question.type === 'display') {
            return (
              <p key={question.id} className={questionLabelClassName}>
                {question.label}
              </p>
            );
          }
          return (
            <div key={question.id} className="mb-3 flex flex-col gap-2.5" data-question-block>
              {question.note && <NoteAlert type="warning" text={question.note} />}
              <div>
                <p className={questionLabelClassName}>{question.label}</p>
                {question.description && (
                  <p className="m-0 mt-1 text-sm text-subtle-foreground">{question.description}</p>
                )}
              </div>
              {question.type === 'select_year' && (
                <YearSelect
                  placeholder={t('medicalProfile.questionnaire.yearPlaceholder')}
                  value={answers[question.id] ? String(answers[question.id]) : undefined}
                  onChange={val => onChange(question.id, Number(val))}
                />
              )}
              {question.type === 'boolean' && (
                <ToggleGroup
                  type="single"
                  variant="outline"
                  className={horizontalGroupClassName}
                  value={toToggleValue(answers[question.id])}
                  onValueChange={value => {
                    if (value) onChange(question.id, value === 'true');
                  }}
                >
                  <ToggleGroupItem value="true" className={horizontalItemClassName}>
                    {t('medicalProfile.questionnaire.yes')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="false" className={horizontalItemClassName}>
                    {t('medicalProfile.questionnaire.no')}
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
              {question.type === 'single_select' && question.choices && question.layout === 'horizontal' && (
                <ToggleGroup
                  type="single"
                  variant="outline"
                  className={horizontalGroupClassName}
                  value={typeof answers[question.id] === 'string' ? (answers[question.id] as string) : ''}
                  onValueChange={value => {
                    if (value) onChange(question.id, value);
                  }}
                >
                  {question.choices.map(c => (
                    <ToggleGroupItem key={c.value} value={c.value} className={horizontalItemClassName}>
                      {c.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              )}
              {question.type === 'single_select' && question.choices && question.layout !== 'horizontal' && (
                <div>
                  {question.choices.map(choice => {
                    const selected = answers[question.id] === choice.value;
                    return (
                      <Button
                        key={choice.value}
                        variant="outline"
                        selected={selected}
                        className={choiceButtonClassName(selected)}
                        onClick={() => onChange(question.id, choice.value)}
                      >
                        {choice.label}
                      </Button>
                    );
                  })}
                </div>
              )}
              {question.type === 'multi_select' && question.choices && question.layout === 'tag_select' && (
                <MultiSelect
                  options={question.choices}
                  placeholder={t('medicalProfile.questionnaire.selectPlaceholder')}
                  aria-label={question.label}
                  value={(answers[question.id] as string[]) || []}
                  onChange={val => onChange(question.id, val)}
                />
              )}
              {question.type === 'multi_select' && question.choices && question.layout !== 'tag_select' && (
                <div>
                  {question.choices.map(choice => {
                    const selected =
                      Array.isArray(answers[question.id]) && (answers[question.id] as string[]).includes(choice.value);
                    return (
                      <Button
                        key={choice.value}
                        variant="outline"
                        selected={selected}
                        className={choiceButtonClassName(selected)}
                        onClick={() => {
                          const current = (answers[question.id] as string[]) || [];
                          if (selected) {
                            onChange(
                              question.id,
                              current.filter(v => v !== choice.value),
                            );
                          } else {
                            onChange(question.id, [...current, choice.value]);
                          }
                        }}
                      >
                        {choice.label}
                      </Button>
                    );
                  })}
                </div>
              )}
              {question.type === 'text' && (
                <Textarea
                  rows={4}
                  className="resize-none"
                  placeholder={t('medicalProfile.questionnaire.textareaPlaceholder' as any)}
                  value={(answers[question.id] as string) || ''}
                  onChange={e => onChange(question.id, e.target.value)}
                />
              )}
              {question.type === 'number' && (
                <Input
                  type="number"
                  inputMode="decimal"
                  className="w-full"
                  min={question.min}
                  max={question.max}
                  step={question.step ?? 1}
                  value={(answers[question.id] as number) ?? ''}
                  onChange={e => onChange(question.id, e.target.value === '' ? null : Number(e.target.value))}
                />
              )}
              {question.conditional_note &&
                Array.isArray(answers[question.id]) &&
                (answers[question.id] as string[]).includes(question.conditional_note.when_contains) && (
                  <NoteAlert type={question.conditional_note.type ?? 'warning'} text={question.conditional_note.note} />
                )}
            </div>
          );
        })}
    </div>
  );
}

export default function MedicalProfileQuestionnaire() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);

  const queryClient = useQueryClient();
  const { requestOptIn } = usePushNotificationOptIn();

  const [response, setResponse] = useState<QuestionnaireResponseDTO | null>(null);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [showGreeting, setShowGreeting] = useState(false);
  // True when the questionnaire is complete but pending_review or in_review — shows the info/processing page.
  const [showProcessingInfo, setShowProcessingInfo] = useState(false);
  // True only when the user just submitted the questionnaire in this session.
  // When re-opening an already-complete questionnaire the form is shown instead.
  const [justCompleted, setJustCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Tracks whether the response was already complete when the page loaded (edit mode).
  const [isEditingCompleted, setIsEditingCompleted] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentSectionIndex]);

  const { isKeyboardVisible } = useIsKeyboardVisible();

  // When the keyboard finishes appearing the viewport is at its final size —
  // scroll the focused element into view now that the layout has settled.
  useEffect(() => {
    if (isKeyboardVisible) {
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        (active?.closest('[data-question-block]') ?? active)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [isKeyboardVisible]);

  const { data: schemaData, isLoading: loadingSchema } = useQuery({
    queryKey: ['questionnaire', 'schema'],
    queryFn: () => ApiQuestionnaireManager.getSchema(),
    staleTime: 1000 * 60 * 60, // schema rarely changes
  });

  const { data: serverResponse, isLoading: loadingResponse } = useQuery<QuestionnaireResponseDTO | null>({
    queryKey: ['questionnaire', 'myResponse', currentUser?.id],
    queryFn: () =>
      ApiQuestionnaireManager.getMyResponse().catch(err => {
        if (err instanceof AxiosError && err.response?.status === 404) return null;
        throw err;
      }),
    staleTime: 1000 * 60 * 5,
  });

  const schema = schemaData?.schema ?? null;
  const totalSections = schema?.sections.length ?? 0;
  const loadingInitial = loadingSchema || loadingResponse;

  // Initialise local state once from server data. A ref guards against
  // re-initialising on background refetches while the user is filling in answers.
  useEffect(() => {
    if (hasInitialized.current || !schemaData || serverResponse === undefined) return;
    hasInitialized.current = true;

    if (serverResponse) {
      setResponse(serverResponse);
      setAnswers(serverResponse.answers);
      if (serverResponse.is_complete) {
        setIsEditingCompleted(true);
        setCurrentSectionIndex(0);
        // For pending_review or in_review states, show the processing info page first.
        if (!serverResponse.reviewed_at) {
          setShowProcessingInfo(true);
        } else {
          setShowGreeting(false);
        }
      } else {
        setCurrentSectionIndex(serverResponse.current_section_index);
        if (serverResponse.current_section_index > 0 || Object.keys(serverResponse.answers).length > 0) {
          setShowGreeting(false);
        } else {
          setShowGreeting(true);
        }
      }
    } else {
      setShowGreeting(true);
    }
  }, [schemaData, serverResponse]);

  const { mutate: createResponse } = useMutation({
    mutationFn: () => ApiQuestionnaireManager.createOrResumeResponse(),
    onSuccess: data => {
      setResponse(data);
      setShowGreeting(false);
      queryClient.setQueryData(['questionnaire', 'myResponse', currentUser?.id], data);
    },
  });

  // Every saved answer feeds the user's interest profile on the backend: drop
  // the suggested articles kept for this session so the next visit to the
  // homepage or the articles page fetches a freshly personalised list.
  const invalidateSuggestedPosts = () => {
    dispatch(contentActions.clearSuggestedPosts());
    clearCache('content.suggestedPosts');
    clearCache('content.searchResults::');
  };

  const updateResponseCache = (patch: Partial<QuestionnaireResponseDTO>) => {
    queryClient.setQueryData(
      ['questionnaire', 'myResponse', currentUser?.id],
      (old: QuestionnaireResponseDTO | null | undefined) => (old ? { ...old, ...patch } : old),
    );
  };

  const saveProgress = (newAnswers: QuestionnaireAnswers, sectionIndex: number) => {
    if (!response) return;
    setIsSaving(true);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      // Optimistically push the latest answers into the shared cache so the
      // homepage card reflects the correct state immediately on navigation.
      updateResponseCache({ answers: newAnswers, current_section_index: sectionIndex });
      ApiQuestionnaireManager.saveProgress(response.id, {
        answers: newAnswers,
        current_section_index: sectionIndex,
      })
        .then(() => {
          invalidateSuggestedPosts();
          setIsSaving(false);
        })
        .catch(() => setIsSaving(false));
    }, 800);
  };

  const handleAnswerChange = (questionId: string, value: QuestionnaireAnswers[string]) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    saveProgress(newAnswers, currentSectionIndex);
  };

  const { isPending: completing, mutate: runComplete } = useMutation({
    mutationFn: () =>
      ApiQuestionnaireManager.complete(response!.id, {
        answers,
        current_section_index: currentSectionIndex,
      }),
    onSuccess: () => {
      posthog.capture('medical_profile.questionnaire_completed');
      invalidateSuggestedPosts();
      queryClient.setQueryData(
        ['questionnaire', 'myResponse', currentUser?.id],
        (old: QuestionnaireResponseDTO | null | undefined) =>
          old ? { ...old, is_complete: true, answers, current_section_index: currentSectionIndex } : old,
      );
      setJustCompleted(true);
      requestOptIn('questionnaire_completed');
    },
  });

  const flushSave = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (!response) return;
    setIsSaving(true);
    // The cache carries the answers before the request returns: leaving the
    // page right away (the home card, a quick return here) must not show the
    // previous answers while the save is still in flight.
    updateResponseCache({ answers, current_section_index: currentSectionIndex });
    try {
      await ApiQuestionnaireManager.saveProgress(response.id, {
        answers,
        current_section_index: currentSectionIndex,
      });
      invalidateSuggestedPosts();
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (!schema) return;
    await flushSave();

    const nextIndex = currentSectionIndex + 1;
    const isLast = nextIndex >= totalSections;

    if (isLast) {
      if (isEditingCompleted) {
        // Already complete: progress was flushed above, just show the completed screen
        setJustCompleted(true);
      } else {
        runComplete();
      }
    } else {
      setCurrentSectionIndex(nextIndex);
      if (response) {
        updateResponseCache({ current_section_index: nextIndex });
        ApiQuestionnaireManager.saveProgress(response.id, {
          answers,
          current_section_index: nextIndex,
        });
      }
    }
  };

  const handlePrev = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
    } else {
      setShowGreeting(true);
    }
  };

  if (loadingInitial) {
    return (
      <div className={pageContainerClassName}>
        <PageTopBar
          title={t('medicalProfile.questionnaire.title')}
          backButton
          onBackButtonClicked={() => navigate(routes.home.route)}
        />
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <Spinner className="size-10 text-foreground" />
          <p className="text-inverse-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (justCompleted) {
    return (
      <div className={pageContainerClassName}>
        <PageTopBar title={t('medicalProfile.questionnaire.title')} />
        <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 py-10 text-center">
          <h1>
            {t('medicalProfile.questionnaire.completed.title')}
            <br />
            <CheckCircleFilled className="animate-in zoom-in-0 spin-in-[-20deg] fill-mode-forwards mt-6 inline-block text-5xl text-foreground duration-[600ms] ease-[cubic-bezier(0.175,0.885,0.32,1.275)]" />{' '}
            <br />
          </h1>
          <p className="text-base leading-normal text-subtle-foreground">
            <Trans i18nKey="medicalProfile.questionnaire.completed.description" components={{ b: <b /> }} />
          </p>
          <Button size="lg" onClick={() => navigate(routes.home.route)}>
            {t('medicalProfile.questionnaire.completed.goHome')}
          </Button>
        </div>
      </div>
    );
  }

  if (showProcessingInfo) {
    return (
      <div className={pageContainerClassName}>
        <PageTopBar
          title={t('medicalProfile.questionnaire.title')}
          backButton
          onBackButtonClicked={() => navigate(routes.home.route)}
        />
        <div className={greetingContainerClassName}>
          <h2>{t('medicalProfile.questionnaire.processingInfo.title')}</h2>
          <ul className={greetingDescriptionListClassName}>
            <li className={greetingDescriptionItemClassName}>
              <MessageOutlined className={greetingDescriptionIconClassName} />
              <p className={greetingDescriptionTextClassName}>
                {<Trans i18nKey="medicalProfile.questionnaire.processingInfo.description1" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={greetingDescriptionItemClassName}>
              <ClockCircleOutlined className={greetingDescriptionIconClassName} />
              <p className={greetingDescriptionTextClassName}>
                {<Trans i18nKey="medicalProfile.questionnaire.processingInfo.description2" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={greetingDescriptionItemClassName}>
              <EditOutlined className={greetingDescriptionIconClassName} />
              <p className={greetingDescriptionTextClassName}>
                {<Trans i18nKey="medicalProfile.questionnaire.processingInfo.description3" components={{ b: <b /> }} />}
              </p>
            </li>
          </ul>
        </div>
        <div className={navigationBarClassName}>
          <Button className="w-full" size="xl" onClick={() => setShowProcessingInfo(false)}>
            {t('medicalProfile.questionnaire.processingInfo.cta')}
          </Button>
        </div>
      </div>
    );
  }

  if (showGreeting) {
    return (
      <div className={pageContainerClassName}>
        <PageTopBar
          title={t('medicalProfile.questionnaire.title')}
          backButton
          onBackButtonClicked={() => navigate(routes.home.route)}
        />
        <div className={greetingContainerClassName}>
          <h2>
            {t('medicalProfile.questionnaire.greeting')}
            <span className="text-foreground">{currentUser?.first_name}.</span>
          </h2>
          <ul className={greetingDescriptionListClassName}>
            <li className={greetingDescriptionItemClassName}>
              <ClockCircleOutlined className={greetingDescriptionIconClassName} />
              <p className={greetingDescriptionTextClassName}>
                {<Trans i18nKey="medicalProfile.questionnaire.description1" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={greetingDescriptionItemClassName}>
              <UserOutlined className={greetingDescriptionIconClassName} />
              <p className={greetingDescriptionTextClassName}>
                {<Trans i18nKey="medicalProfile.questionnaire.description2" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={greetingDescriptionItemClassName}>
              <CheckCircleOutlined className={greetingDescriptionIconClassName} />
              <p className={greetingDescriptionTextClassName}>
                {<Trans i18nKey="medicalProfile.questionnaire.description3" components={{ b: <b /> }} />}
              </p>
            </li>
          </ul>
        </div>
        <div className={navigationBarClassName}>
          <Button
            className="w-full"
            size="xl"
            onClick={() => {
              posthog.capture('medical_profile.questionnaire_started', {
                is_resuming: Boolean(response),
              });
              if (response) {
                setShowGreeting(false);
              } else {
                createResponse();
              }
            }}
          >
            {response ? t('medicalProfile.questionnaire.continue') : t('medicalProfile.questionnaire.start')}
          </Button>
        </div>
      </div>
    );
  }

  if (!schema) return null;

  const currentSection: QuestionnaireSection = schema.sections[currentSectionIndex];
  const isLastSection = currentSectionIndex === totalSections - 1;
  const progressPercent = Math.round((100 * (currentSectionIndex + 1)) / totalSections);

  return (
    <div className={pageContainerClassName}>
      <PageTopBar title={t('medicalProfile.questionnaire.title')} />
      <div className="w-full max-w-content px-5 py-3">
        <Progress value={progressPercent} className="bg-info-muted [&>[data-slot=progress-indicator]]:bg-inverse" />
      </div>
      <div className="flex w-full max-w-content flex-1 flex-col overflow-y-auto" ref={scrollContainerRef}>
        <div className={cn('flex flex-1 flex-col gap-5 px-5 pt-5', isLastSection ? 'pb-0' : 'pb-20')}>
          <p className="my-6 font-semibold tracking-wider text-subtle-foreground uppercase">
            {currentSectionIndex + 1}. {currentSection.label}
          </p>
          <SectionForm section={currentSection} answers={answers} onChange={handleAnswerChange} />
          {isLastSection ? (
            <div className="mt-8 flex flex-col gap-3 pb-10">
              <div>
                <SyncOutlined spin className={cn('mr-2', savingIndicatorClassName(isSaving))} />
              </div>
              <Button className="w-full" size="xl" loading={completing} onClick={handleNext}>
                {isEditingCompleted
                  ? t('medicalProfile.questionnaire.saveEdit')
                  : t('medicalProfile.questionnaire.finish')}
              </Button>
            </div>
          ) : (
            isKeyboardVisible && <NavBar />
          )}
        </div>
      </div>
      {!isKeyboardVisible && <NavBar />}
    </div>
  );

  function NavBar() {
    return (
      <div className={isKeyboardVisible ? navigationBarInlineClassName : navigationBarClassName}>
        {isLastSection ? (
          // Empty div to push the button to the left
          <div></div>
        ) : (
          <div>
            <SyncOutlined spin className={cn('mr-4', savingIndicatorClassName(isSaving))} />
            <Button className="flex-1" size="lg" loading={completing} onClick={handleNext}>
              {t('medicalProfile.questionnaire.save')}
            </Button>
          </div>
        )}
        <Button variant="outline" size="icon" onClick={handlePrev}>
          <LeftOutlined />
        </Button>
      </div>
    );
  }
}
